import { useEffect } from "react"
import { useAppDispatch, useAppSelector } from "../store"
import { socket } from "."
import { chatApi } from "../chat/api"

const SocketManager = () => {
  const { isAuthenticated, accessToken } = useAppSelector((s) => s.auth)
  const dispatch = useAppDispatch()

  useEffect(() => {
    if (!isAuthenticated || !accessToken) {
      socket.disconnect()
      return
    }

    socket.auth = {
      token: accessToken,
    }

    socket.connect()

    return () => {
      socket.disconnect()
    }
  }, [isAuthenticated, accessToken])

  // Nothing else in the app surfaces socket connection state, so failures (bad auth,
  // no websocket upgrade, dropped connection) fail completely silently otherwise.
  useEffect(() => {
    const handleConnect = () => {
      // eslint-disable-next-line no-console
      console.debug("[socket] connected", socket.id, "transport:", socket.io.engine?.transport?.name)
    }
    const handleDisconnect = (reason: string) => {
      // eslint-disable-next-line no-console
      console.debug("[socket] disconnected:", reason)
    }
    const handleConnectError = (error: Error) => {
      // eslint-disable-next-line no-console
      console.error("[socket] connect_error:", error.message)
    }

    socket.on("connect", handleConnect)
    socket.on("disconnect", handleDisconnect)
    socket.on("connect_error", handleConnectError)

    return () => {
      socket.off("connect", handleConnect)
      socket.off("disconnect", handleDisconnect)
      socket.off("connect_error", handleConnectError)
    }
  }, [])

  // Pushed whenever a chat we're part of gets a new message or a new chat is created
  // for us — a "go refetch" signal, not authoritative data. Not scoped to any chat being open, unlike
  // message:new, so this is the one listener that has to live at the app-shell level.
  useEffect(() => {
    const handleChatUpdated = () => {
      dispatch(chatApi.util.invalidateTags(["Chat"]))
    }

    socket.on("chat:updated", handleChatUpdated)

    return () => {
      socket.off("chat:updated", handleChatUpdated)
    }
  }, [dispatch])

  return null
}

export default SocketManager
